<template>
  <div class="pl-legal">
    <header class="pl-legal-header">
      <router-link to="/" class="pl-legal-brand">
        <img src="@/assets/ProfitLedgex.png" alt="ProfitLedgex" class="pl-legal-logo" />
        <span>ProfitLedgex</span>
      </router-link>
      <a href="#" class="pl-legal-back" @click.prevent="goBack">Back to sign up</a>
    </header>

    <main class="pl-legal-content">
      <h1>Privacy Policy</h1>
      <p class="pl-legal-meta">Version {{ version }} &middot; Last updated {{ lastUpdated }}</p>

      <p>
        This Privacy Policy explains what information ProfitLedgex ("we", "us", "our")
        collects when you use our Service, how we use and protect it, and the choices you
        have. By creating an account, you agree to the collection and use of information as
        described here.
      </p>

      <h2>1. Information We Collect</h2>
      <p>When you create and use an account, we collect:</p>
      <ul>
        <li><strong>Account information:</strong> name, email address, phone number, username (if chosen), and a securely hashed password.</li>
        <li><strong>Business &amp; financial data you enter:</strong> customer records, products, invoices, transactions, GST/tax details, and bank account or UPI details you choose to store for use on your invoices.</li>
        <li><strong>Usage data:</strong> log data such as sign-in times and basic device/browser information, used for security and troubleshooting.</li>
        <li><strong>Support communications:</strong> information you provide when contacting support or raising a ticket.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Create and manage your account, including email verification via one-time passcodes;</li>
        <li>Provide the core Service — invoicing, customer and product management, transaction tracking, and reporting;</li>
        <li>Populate invoices and business documents with the bank/business details you provide;</li>
        <li>Send account-related communications, such as OTPs, trial and subscription reminders, and support responses;</li>
        <li>Maintain the security and integrity of the Service, including detecting fraud or abuse;</li>
        <li>Comply with applicable legal and tax obligations.</li>
      </ul>
      <p>We do not sell your personal data.</p>

      <h2>3. Bank Account &amp; Financial Data</h2>
      <p>
        Bank account and UPI details you enter are stored so they can be displayed on invoices
        you issue to your own customers. This data is stored in our database with the same
        access controls as the rest of your account data, is never displayed to anyone other
        than you and, where you choose to share an invoice, the customer named on that
        invoice. We do not use your bank details to initiate any transaction, debit, or
        transfer.
      </p>

      <h2>4. How We Protect Your Information</h2>
      <p>
        Passwords are hashed using industry-standard one-way hashing (bcrypt) and are never
        stored or transmitted in plain text. Data in transit is protected using SSL/TLS
        encryption. Access to production data is restricted to authorized personnel who need
        it to operate and support the Service. No method of storage or transmission is 100%
        secure, and we cannot guarantee absolute security.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain your account and business data for as long as your account is active, and
        for a reasonable period afterward to comply with legal, tax, and accounting
        obligations, resolve disputes, and enforce our agreements. You may request deletion of
        your account as described in Section 7 below.
      </p>

      <h2>6. Sharing of Information</h2>
      <p>We do not share your personal data with third parties except:</p>
      <ul>
        <li>With service providers who help us operate the Service (e.g. email delivery for OTPs and notifications, payment processing for subscriptions), bound by confidentiality obligations;</li>
        <li>Where required by law, regulation, legal process, or governmental request;</li>
        <li>To protect the rights, property, or safety of ProfitLedgex, our users, or the public;</li>
        <li>With your explicit consent.</li>
      </ul>

      <h2>7. Your Rights &amp; Choices</h2>
      <p>Subject to applicable law, you have the right to:</p>
      <ul>
        <li>Access and review the personal data associated with your account;</li>
        <li>Correct inaccurate information via your account settings;</li>
        <li>Request export of your data;</li>
        <li>Request deletion of your account and associated data, subject to legal/tax retention requirements;</li>
      </ul>
      <p>
        To exercise these rights, contact us through the
        <router-link to="/support">Support</router-link> section of the app.
      </p>

      <h2>8. Cookies &amp; Sessions</h2>
      <p>
        We use session cookies to keep you signed in and to maintain the security of your
        session. We do not use third-party advertising or tracking cookies.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is not directed to individuals under 18. We do not knowingly collect
        personal information from children.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be
        reflected by an updated "Last updated" date and version number above. Continued use of
        the Service after changes take effect constitutes acceptance of the revised policy.
      </p>

      <h2>11. Governing Law</h2>
      <p>
        This Policy is governed by the laws of India. Any disputes arising from this Policy
        shall be subject to the exclusive jurisdiction of the courts located in India.
      </p>

      <h2>12. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or how your data is handled, please
        contact us through the <router-link to="/support">Support</router-link> section of the
        app.
      </p>
    </main>
  </div>
</template>

<script>
import { TERMS_VERSION, TERMS_LAST_UPDATED } from '@/utils/legal';

export default {
  name: 'PrivacyPage',
  data: () => ({
    version: TERMS_VERSION,
    lastUpdated: TERMS_LAST_UPDATED,
  }),
  methods: {
    goBack() {
      // Opened via target="_blank" from signup — close this tab so the
      // signup form (with whatever the user already typed) is untouched,
      // rather than navigating in-tab and losing it. Falls back to a normal
      // navigation if the tab wasn't opened by script (e.g. visited directly
      // or opened by the browser in a way close() can't affect).
      window.close();
      if (!window.closed) {
        this.$router.push('/signup');
      }
    },
  },
};
</script>

<style scoped>
.pl-legal {
  min-height: 100vh;
  background: #f4f6f9;
  font-family: 'Manrope', system-ui, sans-serif;
  color: #0e2433;
}

.pl-legal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 32px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8ee;
  position: sticky;
  top: 0;
  z-index: 5;
}

.pl-legal-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #0b1e2d;
  font-weight: 800;
  font-size: 17px;
}

.pl-legal-logo {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  object-fit: cover;
}

.pl-legal-back {
  color: #0a6cc0;
  font-weight: 700;
  font-size: 13.5px;
  text-decoration: none;
}
.pl-legal-back:hover { text-decoration: underline; }

.pl-legal-content {
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  line-height: 1.65;
}

.pl-legal-content h1 {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
}

.pl-legal-meta {
  color: #64747f;
  font-size: 13px;
  margin: 0 0 28px;
}

.pl-legal-content h2 {
  font-size: 18px;
  font-weight: 700;
  margin: 30px 0 10px;
  color: #0b1e2d;
}

.pl-legal-content p {
  font-size: 14.5px;
  color: #374955;
  margin: 0 0 14px;
}

.pl-legal-content ul {
  margin: 0 0 14px;
  padding-left: 22px;
}

.pl-legal-content li {
  font-size: 14.5px;
  color: #374955;
  margin-bottom: 6px;
}

.pl-legal-content a {
  color: #0a6cc0;
  font-weight: 600;
  text-decoration: none;
}
.pl-legal-content a:hover { text-decoration: underline; }
</style>
